import type { VoiceSpan } from "Infrastructure/Api/Api";
import {
  VoiceReactionPacingRhythmTag,
  VoiceReactionTag,
  VoiceReactionVolumeEnergyTag,
  VoiceStyle,
} from "Infrastructure/Api/Api";
import { Resources } from "Infrastructure/Translations/Resources";

/**
 * A reaction span is an inline marker (ElevenLabs v3 audio tag) with no text.
 * Its contribution to flat text is zero — it lives between characters.
 */
export function isReactionSpan(span: VoiceSpan): boolean {
  return (
    span.reaction != null ||
    span.reactionVolumeEnergy != null ||
    span.reactionPacingRhythm != null ||
    (span.customReaction != null && span.customReaction.length > 0)
  );
}

export function isTextSpan(span: VoiceSpan): boolean {
  return !isReactionSpan(span);
}

/** Concatenates all text-span texts into a single string. Reaction spans contribute nothing. */
export function flattenSpans(spans: VoiceSpan[]): string {
  return spans.map((s) => s.text).join("");
}

// ---------------------------------------------------------------------------
// Reaction span helpers
// ---------------------------------------------------------------------------

/**
 * Translator signature compatible with `useResource().t`. The `key` is typed
 * as `any` so callers can pass the typed Resources proxy values (ObjPathProxy)
 * without `spanUtils` having to depend on translation infrastructure.
 */
export type ReactionLabelTranslator = (key: any) => string;

/** Empty reaction span (optionally pre-filled with a custom label). */
export function blankReactionSpan(
  customReaction: string | null = null,
): VoiceSpan {
  return {
    text: "",
    style: null,
    reaction: null,
    reactionVolumeEnergy: null,
    reactionPacingRhythm: null,
    customReaction,
  };
}

/** Display label for a reaction span — custom text or translated enum value. */
export function labelOfReactionSpan(
  span: VoiceSpan,
  t: ReactionLabelTranslator,
): string {
  if (span.customReaction) return span.customReaction;
  const pick =
    span.reaction ?? span.reactionVolumeEnergy ?? span.reactionPacingRhythm;
  if (!pick) return "";
  return t(
    Resources.JobDetail.Reaction.Values[
      pick as keyof typeof Resources.JobDetail.Reaction.Values
    ],
  );
}

/**
 * Matches a typed bracket label against translated enum values; falls back to
 * a custom reaction span carrying the literal label as `customReaction`.
 */
export function resolveReactionFromLabel(
  label: string,
  t: ReactionLabelTranslator,
): VoiceSpan {
  const tryEnum = <E extends string>(
    values: Record<string, E>,
    apply: (v: E) => Partial<VoiceSpan>,
  ): VoiceSpan | null => {
    for (const v of Object.values(values)) {
      const enumLabel = t(
        Resources.JobDetail.Reaction.Values[
          v as keyof typeof Resources.JobDetail.Reaction.Values
        ],
      );
      if (enumLabel === label) return { ...blankReactionSpan(), ...apply(v) };
    }
    return null;
  };
  return (
    tryEnum(VoiceReactionTag, (v) => ({ reaction: v })) ??
    tryEnum(VoiceReactionVolumeEnergyTag, (v) => ({
      reactionVolumeEnergy: v,
    })) ??
    tryEnum(VoiceReactionPacingRhythmTag, (v) => ({
      reactionPacingRhythm: v,
    })) ??
    blankReactionSpan(label)
  );
}

/**
 * Splits a span list at `cursorOffset` (clean-text coordinates, ignoring
 * reaction spans) and inserts an empty reaction span at that point. If the
 * cursor falls past the end, the reaction is appended.
 */
export function insertReactionAtOffset(
  spans: VoiceSpan[],
  cursorOffset: number,
): { spans: VoiceSpan[]; insertedIndex: number } {
  const out: VoiceSpan[] = [];
  let offset = 0;
  let insertedIndex = -1;
  const reaction = blankReactionSpan();
  for (const span of spans) {
    if (isReactionSpan(span)) {
      out.push(span);
      continue;
    }
    const spanEnd = offset + span.text.length;
    if (insertedIndex === -1 && cursorOffset <= spanEnd) {
      const cutLocal = Math.max(0, cursorOffset - offset);
      const left = span.text.slice(0, cutLocal);
      const right = span.text.slice(cutLocal);
      if (left.length > 0) out.push({ text: left, style: span.style });
      insertedIndex = out.length;
      out.push(reaction);
      if (right.length > 0) out.push({ text: right, style: span.style });
    } else {
      out.push(span);
    }
    offset = spanEnd;
  }
  if (insertedIndex === -1) {
    insertedIndex = out.length;
    out.push(reaction);
  }
  return { spans: out, insertedIndex };
}

// ---------------------------------------------------------------------------
// Edit-mode bracket representation
//
// When a VoiceSegment is being edited in a contenteditable, reactions need to
// stay visible so the user knows where they are anchored. We render each
// reaction as a `[Label]` token inline (same UX pattern as `{{variable}}`).
// On commit the tokens are matched back to the original reaction spans by
// label and translated to real reaction spans. Bracket text typed by the
// user that doesn't match any original reaction is kept as literal text.
// ---------------------------------------------------------------------------

export type ReactionLabelResolver = (span: VoiceSpan) => string;

/**
 * Converts a bracket label typed by the user into a reaction span. Called by
 * `reconciledSpansFromEditText` for brackets that don't match an existing
 * reaction by label. Typically tries to map the label to an enum value first
 * (so `[Křik]` becomes `reactionVolumeEnergy: Shouting`) and falls back to
 * `customReaction` for anything else. When `undefined`, unmatched brackets
 * stay as literal text.
 */
export type ReactionLabelToSpanResolver = (label: string) => VoiceSpan;

const BRACKET_RE_SOURCE = String.raw`\[([^\[\]]+)\]`;

/** Edit-mode flat text — text spans verbatim, reactions as `[Label]`. */
export function buildEditModeText(
  spans: VoiceSpan[],
  labelOf: ReactionLabelResolver,
): string {
  return spans
    .map((s) => (isReactionSpan(s) ? `[${labelOf(s)}]` : s.text))
    .join("");
}

interface StrippedEditText {
  cleanText: string;
  /** Translates an offset in `editText` to the matching offset in `cleanText`. */
  cleanAt: (editOffset: number) => number;
  tokens: {
    editStart: number;
    editEnd: number;
    label: string;
    cleanAt: number;
  }[];
}

/** Removes `[...]` tokens from the edit text and builds a position-mapping. */
export function stripReactionBrackets(editText: string): StrippedEditText {
  const tokens: StrippedEditText["tokens"] = [];
  let clean = "";
  let cursor = 0;
  const re = new RegExp(BRACKET_RE_SOURCE, "g");
  let m: RegExpExecArray | null = re.exec(editText);
  while (m !== null) {
    clean += editText.slice(cursor, m.index);
    tokens.push({
      editStart: m.index,
      editEnd: m.index + m[0].length,
      label: m[1],
      cleanAt: clean.length,
    });
    cursor = m.index + m[0].length;
    m = re.exec(editText);
  }
  clean += editText.slice(cursor);
  const cleanAt = (editOffset: number): number => {
    let delta = 0;
    for (const tok of tokens) {
      if (tok.editStart >= editOffset) break;
      if (tok.editEnd <= editOffset) {
        delta += tok.editEnd - tok.editStart;
      } else {
        // Cursor lands inside the bracket — snap to the bracket's clean-text
        // position (i.e. just before the reaction).
        return tok.cleanAt;
      }
    }
    return editOffset - delta;
  };
  return { cleanText: clean, cleanAt, tokens };
}

/**
 * Parses edit-mode text back to spans. Bracket tokens are matched to original
 * reactions by label (first-come-first-served per label). Surviving reactions
 * land at their bracket position; deleted ones are dropped. Brackets with
 * labels not found in the original reactions stay as literal text.
 */
export function reconciledSpansFromEditText(
  oldSpans: VoiceSpan[],
  newEditText: string,
  labelOf: ReactionLabelResolver,
  resolveNewReaction?: ReactionLabelToSpanResolver,
): VoiceSpan[] {
  const stripped = stripReactionBrackets(newEditText);
  const { tokens } = stripped;

  const pools = new Map<string, VoiceSpan[]>();
  for (const r of oldSpans) {
    if (!isReactionSpan(r)) continue;
    const lbl = labelOf(r);
    let pool = pools.get(lbl);
    if (!pool) {
      pool = [];
      pools.set(lbl, pool);
    }
    pool.push(r);
  }

  const matched: { span: VoiceSpan; cleanAt: number }[] = [];
  const unmatchedLiteral: StrippedEditText["tokens"] = [];
  for (const tok of tokens) {
    const pool = pools.get(tok.label);
    const claimed = pool && pool.length > 0 ? pool.shift() : undefined;
    if (claimed) {
      matched.push({ span: claimed, cleanAt: tok.cleanAt });
    } else if (resolveNewReaction) {
      // User typed or edited a bracket that doesn't match any existing
      // reaction — synthesize a fresh reaction span (enum if label is a
      // known enum translation, otherwise customReaction).
      matched.push({
        span: resolveNewReaction(tok.label),
        cleanAt: tok.cleanAt,
      });
    } else {
      unmatchedLiteral.push(tok);
    }
  }

  // Restore literal bracket text only for tokens we chose not to convert.
  // Iterate in descending clean offset so earlier positions stay valid while
  // we splice.
  let effectiveCleanText = stripped.cleanText;
  const sortedUnmatched = [...unmatchedLiteral].sort(
    (a, b) => b.cleanAt - a.cleanAt,
  );
  for (const tok of sortedUnmatched) {
    const literal = `[${tok.label}]`;
    effectiveCleanText =
      effectiveCleanText.slice(0, tok.cleanAt) +
      literal +
      effectiveCleanText.slice(tok.cleanAt);
    for (const m of matched) {
      if (m.cleanAt >= tok.cleanAt) m.cleanAt += literal.length;
    }
  }

  const oldTextSpans = oldSpans.filter((s) => !isReactionSpan(s));
  const newTextSpans = reconciledSpans(oldTextSpans, effectiveCleanText);

  if (matched.length === 0) return newTextSpans;

  matched.sort((a, b) => a.cleanAt - b.cleanAt);
  const out: VoiceSpan[] = [];
  let cursor = 0;
  let rIdx = 0;

  // Reactions that land at position 0 (before any text).
  while (rIdx < matched.length && matched[rIdx].cleanAt === 0) {
    out.push(matched[rIdx].span);
    rIdx++;
  }

  for (const s of newTextSpans) {
    const spanStart = cursor;
    const spanEnd = cursor + s.text.length;
    let localCursor = 0;
    // Reactions falling inside (spanStart, spanEnd]. Each one splits the
    // current text span into "before reaction" / reaction / "after".
    while (rIdx < matched.length && matched[rIdx].cleanAt <= spanEnd) {
      const localAt = matched[rIdx].cleanAt - spanStart;
      if (localAt > localCursor) {
        out.push({
          text: s.text.slice(localCursor, localAt),
          style: s.style,
        });
      }
      out.push(matched[rIdx].span);
      localCursor = localAt;
      rIdx++;
    }
    if (localCursor < s.text.length) {
      out.push({ text: s.text.slice(localCursor), style: s.style });
    }
    cursor = spanEnd;
  }
  // Trailing reactions (positions beyond the final text) — append.
  while (rIdx < matched.length) {
    out.push(matched[rIdx].span);
    rIdx++;
  }
  return out;
}

/**
 * Extracts the portion of `spans` covering character range [start, end),
 * preserving the emotion style of each text span AND any reaction spans
 * positioned inside the range.
 *
 * Reaction span placement rule: a reaction at offset p belongs to the slice
 * when `start <= p < end` (left-inclusive). At the trailing end of the overall
 * text (p === totalLen), the reaction is kept with the final slice.
 */
export function sliceSpans(
  spans: VoiceSpan[],
  start: number,
  end: number,
): VoiceSpan[] {
  const totalLen = flattenSpans(spans).length;
  const includeTail = end >= totalLen;

  const result: VoiceSpan[] = [];
  let offset = 0;
  for (const span of spans) {
    if (isReactionSpan(span)) {
      const inRange =
        offset >= start && (offset < end || (includeTail && offset === end));
      if (inRange) result.push(span);
      continue;
    }
    const spanEnd = offset + span.text.length;
    if (spanEnd > start && offset < end) {
      const sliceStart = Math.max(offset, start);
      const sliceEnd = Math.min(spanEnd, end);
      result.push({
        text: span.text.slice(sliceStart - offset, sliceEnd - offset),
        style: span.style,
      });
    }
    offset = spanEnd;
  }
  return result.length > 0 ? result : [{ text: "", style: null }];
}

/**
 * Applies `style` to the character range [selStart, selEnd] within spans.
 * Splits text spans at the selection boundaries, assigns the new style to the
 * covered portion, then merges adjacent spans that share the same style.
 * Reaction spans inside the range are preserved untouched.
 */
export function applyStyleToRange(
  spans: VoiceSpan[],
  selStart: number,
  selEnd: number,
  style: VoiceStyle | null,
): VoiceSpan[] {
  if (selStart >= selEnd) return spans;

  const result: VoiceSpan[] = [];
  let offset = 0;

  for (const span of spans) {
    if (isReactionSpan(span)) {
      result.push(span);
      continue;
    }
    const spanStart = offset;
    const spanEnd = offset + span.text.length;

    if (spanEnd <= selStart || spanStart >= selEnd) {
      // Entirely outside selection — keep as-is
      result.push(span);
    } else {
      // Before selection within this span
      if (spanStart < selStart) {
        result.push({
          text: span.text.slice(0, selStart - spanStart),
          style: span.style,
        });
      }
      // Portion inside selection
      const insideStart = Math.max(spanStart, selStart);
      const insideEnd = Math.min(spanEnd, selEnd);
      result.push({
        text: span.text.slice(insideStart - spanStart, insideEnd - spanStart),
        style,
      });
      // After selection within this span
      if (spanEnd > selEnd) {
        result.push({
          text: span.text.slice(selEnd - spanStart),
          style: span.style,
        });
      }
    }

    offset = spanEnd;
  }

  return mergeAdjacentTextSpans(
    result.filter((s) => isReactionSpan(s) || s.text.length > 0),
  );
}

/**
 * Normalise style: treat VoiceStyle.Neutral the same as null so that
 * adjacent spans with Neutral vs null correctly merge.
 */
function normaliseStyle(
  style: VoiceStyle | null | undefined,
): VoiceStyle | null {
  if (style == null || style === VoiceStyle.Neutral) return null;
  return style;
}

/**
 * Merge adjacent text spans that share the same normalised style.
 * Reaction spans act as barriers — they are never merged and never allow
 * text spans on either side of them to merge across.
 */
function mergeAdjacentTextSpans(spans: VoiceSpan[]): VoiceSpan[] {
  const merged: VoiceSpan[] = [];
  for (const span of spans) {
    if (isReactionSpan(span)) {
      merged.push(span);
      continue;
    }
    const normStyle = normaliseStyle(span.style);
    const last = merged[merged.length - 1];
    if (
      last &&
      !isReactionSpan(last) &&
      normaliseStyle(last.style) === normStyle
    ) {
      merged[merged.length - 1] = { ...last, text: last.text + span.text };
    } else {
      merged.push({ ...span, style: normStyle });
    }
  }
  return merged;
}

/**
 * Returns the style of the text span that contains character position `pos`.
 * Used to inherit the style when text is inserted within an existing span.
 * Returns null (Neutral) if no text spans exist or pos is at the very end.
 */
function styleAtPosition(
  textSpans: VoiceSpan[],
  pos: number,
): VoiceStyle | null {
  let offset = 0;
  for (const span of textSpans) {
    const spanEnd = offset + span.text.length;
    if (pos < spanEnd) {
      return normaliseStyle(span.style);
    }
    offset = spanEnd;
  }
  return normaliseStyle(textSpans[textSpans.length - 1]?.style);
}

/**
 * Reconciles the span structure after the flat text was edited in a
 * contenteditable. Reaction spans are preserved at their relative positions
 * in the flat text (reactions have zero width, so they "stick" to offsets
 * that survived the diff). Text spans in unchanged prefix/suffix regions
 * keep their style; the changed middle section collapses to a single span
 * inheriting the style of whatever text span straddles the change start.
 */
export function reconciledSpans(
  oldSpans: VoiceSpan[],
  newFlatText: string,
): VoiceSpan[] {
  const oldFlat = flattenSpans(oldSpans);

  // Extract reactions with their flat-text offsets; keep text spans in order.
  const reactions: { span: VoiceSpan; pos: number }[] = [];
  const textSpans: VoiceSpan[] = [];
  {
    let offset = 0;
    for (const s of oldSpans) {
      if (isReactionSpan(s)) {
        reactions.push({ span: s, pos: offset });
      } else {
        textSpans.push(s);
        offset += s.text.length;
      }
    }
  }

  // Fast path: no text change — return original array, reference-stable.
  if (oldFlat === newFlatText) return oldSpans;

  // Diff prefix/suffix against the text-only flat view.
  let prefixLen = 0;
  while (
    prefixLen < oldFlat.length &&
    prefixLen < newFlatText.length &&
    oldFlat[prefixLen] === newFlatText[prefixLen]
  ) {
    prefixLen++;
  }
  let suffixLen = 0;
  while (
    suffixLen < oldFlat.length - prefixLen &&
    suffixLen < newFlatText.length - prefixLen &&
    oldFlat[oldFlat.length - 1 - suffixLen] ===
      newFlatText[newFlatText.length - 1 - suffixLen]
  ) {
    suffixLen++;
  }

  const oldChangedEnd = oldFlat.length - suffixLen;
  const newChangedPart = newFlatText.slice(
    prefixLen,
    newFlatText.length - suffixLen,
  );
  const inheritedStyle = styleAtPosition(textSpans, prefixLen);

  // Rebuild text-only spans covering the new flat text by emitting: prefix
  // slices → the changed middle section (once) → suffix slices.
  //
  // We must insert the changed part BEFORE the first suffix span; routing a
  // span to "suffix" and then appending the change afterwards would produce
  // the change at the wrong end of the text (e.g. inserting "X" at position 0
  // of "abc" wrongly yielded "abcX").
  const newTextSpans: VoiceSpan[] = [];
  let offset = 0;
  let changedInserted = false;
  const emitChangedIfDue = () => {
    if (!changedInserted && newChangedPart.length > 0) {
      newTextSpans.push({ text: newChangedPart, style: inheritedStyle });
      changedInserted = true;
    }
  };
  for (const span of textSpans) {
    const spanStart = offset;
    const spanEnd = offset + span.text.length;

    if (spanEnd <= prefixLen) {
      newTextSpans.push(span);
    } else if (spanStart >= oldChangedEnd) {
      emitChangedIfDue();
      newTextSpans.push(span);
    } else {
      if (spanStart < prefixLen) {
        newTextSpans.push({
          text: span.text.slice(0, prefixLen - spanStart),
          style: span.style,
        });
      }
      emitChangedIfDue();
      if (spanEnd > oldChangedEnd) {
        newTextSpans.push({
          text: span.text.slice(oldChangedEnd - spanStart),
          style: span.style,
        });
      }
    }
    offset = spanEnd;
  }
  emitChangedIfDue();

  // Map each old reaction position to a position in newFlatText and
  // interleave with the rebuilt text spans.
  const oldToNew = (oldPos: number): number => {
    if (oldPos <= prefixLen) return oldPos;
    if (oldPos >= oldChangedEnd)
      return newFlatText.length - (oldFlat.length - oldPos);
    // Inside the changed region — snap to the start of the change.
    return prefixLen;
  };

  const mergedText = mergeAdjacentTextSpans(
    newTextSpans.filter((s) => s.text.length > 0),
  );
  if (reactions.length === 0) return mergedText;

  const reactionsInOrder = reactions
    .map((r) => ({ span: r.span, pos: oldToNew(r.pos) }))
    .sort((a, b) => a.pos - b.pos);

  // Walk mergedText and interleave reactions. When a reaction position falls
  // INSIDE a text span's range (rather than at a boundary), split the span
  // there — otherwise merged text spans would push the reaction off to the
  // end of the merged block.
  const out: VoiceSpan[] = [];
  let cursor = 0;
  let rIdx = 0;
  while (rIdx < reactionsInOrder.length && reactionsInOrder[rIdx].pos === 0) {
    out.push(reactionsInOrder[rIdx].span);
    rIdx++;
  }
  for (const s of mergedText) {
    const spanStart = cursor;
    const spanEnd = cursor + s.text.length;
    let localCursor = 0;
    while (
      rIdx < reactionsInOrder.length &&
      reactionsInOrder[rIdx].pos <= spanEnd
    ) {
      const localAt = reactionsInOrder[rIdx].pos - spanStart;
      if (localAt > localCursor) {
        out.push({
          text: s.text.slice(localCursor, localAt),
          style: s.style,
        });
      }
      out.push(reactionsInOrder[rIdx].span);
      localCursor = localAt;
      rIdx++;
    }
    if (localCursor < s.text.length) {
      out.push({ text: s.text.slice(localCursor), style: s.style });
    }
    cursor = spanEnd;
  }
  while (rIdx < reactionsInOrder.length) {
    out.push(reactionsInOrder[rIdx].span);
    rIdx++;
  }

  return out;
}

// ---------------------------------------------------------------------------
// Emotion display helpers
// ---------------------------------------------------------------------------

export interface EmotionMeta {
  style: VoiceStyle;
  emoji: string;
  color: string;
}

export const EMOTION_META: Record<VoiceStyle, EmotionMeta> = {
  [VoiceStyle.Neutral]: {
    style: VoiceStyle.Neutral,
    emoji: "",
    color: "transparent",
  },
  // Lemon yellow
  [VoiceStyle.Cheerful]: {
    style: VoiceStyle.Cheerful,
    emoji: "😄",
    color: "rgba(255, 214, 0, 0.25)",
  },
  // Deep orange
  [VoiceStyle.Excited]: {
    style: VoiceStyle.Excited,
    emoji: "🤩",
    color: "rgba(255, 87, 34, 0.25)",
  },
  // Green
  [VoiceStyle.Friendly]: {
    style: VoiceStyle.Friendly,
    emoji: "😊",
    color: "rgba(76, 175, 80, 0.25)",
  },
  // Sky blue
  [VoiceStyle.Sad]: {
    style: VoiceStyle.Sad,
    emoji: "😢",
    color: "rgba(66, 165, 245, 0.25)",
  },
  // Red
  [VoiceStyle.Angry]: {
    style: VoiceStyle.Angry,
    emoji: "😤",
    color: "rgba(244, 67, 54, 0.28)",
  },
  // Cyan-teal (distinct from green)
  [VoiceStyle.Calm]: {
    style: VoiceStyle.Calm,
    emoji: "😌",
    color: "rgba(38, 198, 218, 0.25)",
  },
  // Indigo (distinct from sky blue)
  [VoiceStyle.Newscast]: {
    style: VoiceStyle.Newscast,
    emoji: "📰",
    color: "rgba(57, 73, 171, 0.32)",
  },
  // Soft purple (distinct from indigo)
  [VoiceStyle.Whispering]: {
    style: VoiceStyle.Whispering,
    emoji: "🤫",
    color: "rgba(149, 117, 205, 0.30)",
  },
  // Deep amber (distinct from yellow Cheerful)
  [VoiceStyle.Authoritative]: {
    style: VoiceStyle.Authoritative,
    emoji: "🎙️",
    color: "rgba(255, 143, 0, 0.25)",
  },
  // Hot pink (distinct from everything)
  [VoiceStyle.Empathetic]: {
    style: VoiceStyle.Empathetic,
    emoji: "🤗",
    color: "rgba(236, 64, 122, 0.25)",
  },
};

export function getEmotionMeta(
  style: VoiceStyle | null | undefined,
): EmotionMeta {
  if (style == null) return EMOTION_META[VoiceStyle.Neutral];
  return EMOTION_META[style] ?? EMOTION_META[VoiceStyle.Neutral];
}

// ---------------------------------------------------------------------------
// Contenteditable selection helpers
// ---------------------------------------------------------------------------

/**
 * Returns the character offsets of the current DOM selection within a
 * contenteditable element, or null when there is no selection or the
 * caret/selection is outside the element.
 *
 * Centralises the repeated range.cloneRange() / preStart pattern so that
 * callers don't need to duplicate the same eight lines every time they need
 * character-level offsets from a DOM selection.
 */
export function getSelectionCharOffsets(
  el: HTMLElement,
): { selStart: number; selEnd: number; selectedText: string } | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) {
    return null;
  }
  const range = sel.getRangeAt(0);
  const preStart = range.cloneRange();
  preStart.selectNodeContents(el);
  preStart.setEnd(range.startContainer, range.startOffset);
  const selStart = preStart.toString().length;
  return {
    selStart,
    selEnd: selStart + range.toString().length,
    selectedText: range.toString().trim(),
  };
}
